/**
 * 27 - Downed Pilot (10 §2): a ruined hamlet north of the belt, nobody's compound, and a pilot with a bad
 * leg lying up behind one of its houses. Find him, get him on his feet - he walks, he never runs - and
 * bring him to the Osprey. Search parties come in from the map's edges on a clock, wave after wave, and make
 * for where he is hiding: if any soldier comes within three metres of him first, he is lost.
 *
 * The first wave's time is set by `finish` from the walk the finished map offers (helpers3bTime.ts); the
 * others follow at fixed intervals, and keep coming while he is being walked out.
 */

import { EXPANSION } from '../../sim/tuning';
import type { PropDef } from '../../sim/types';
import type { Point } from '../types';
import { checker, insideRect, required, structure } from './helpers';
import { FIRST_CAPTIVE_ID, flagTargets, keepClear, RECALL, standNear, wokenBy } from './helpers3b';
import { searchParty } from './helpers3bSearch';
import { carefulSeconds, fairTimer, type TimedRule } from './helpers3bTime';
import type { ArchetypeSpec } from './types';

const WAVE_GAP = 75;
/** Nobody the garrison posts stands this close to him: a sentry at his elbow would "find" him by standing still. */
const PRIVACY = 4.5;

export const wavesFor = (threat: number): number => (threat >= 5 ? 3 : 2);
export const menPerWave = (threat: number): number => 2 + Math.floor(threat / 4);
const tagOf = (wave: number): string => `searchParty${wave + 1}`;

const searchRules = (def: { objective: { rules: unknown[] } }): TimedRule[] =>
  (def.objective.rules as TimedRule[]).filter((r) => r.type === 'timedEvent' && r.effect === 'searchPartyArrives');

export const downedPilot: ArchetypeSpec = {
  id: 'downed-pilot',
  name: 'DOWNED PILOT',
  nativeBiome: 'desert',
  baseThreat: 4,
  lengthTiles: 160,
  clockScale: 1,
  startAlert: 0,
  garrison: 'regular',
  perimeter: 'none',
  sealed: false,
  layout: 'compound',
  size: { w: [46, 54], d: [28, 32] },
  river: false,
  patrolLoop: 'outside',
  breach: { min: 0, max: 0 },

  build(g) {
    // The ruins: the engine has no ruin-cluster prop, so the hamlet is composed - houses, then lengths of broken wall.
    const placed: Point[] = [];
    const houses: PropDef[] = [];
    const wanted = 4 + g.rng.int(3);
    for (let i = 0; i < wanted; i++) {
      const o = { minDist: 9, from: placed, margin: 1.4 };
      const house = i < 3 ? required(g, 'ruinHouse', o) : structure(g, 'ruinHouse', o);
      if (house && house.id >= 0) houses.push(house);
    }
    const walls = 4 + g.rng.int(3);
    for (let i = 0; i < walls; i++) structure(g, 'ruinWall', { length: Math.round(g.rng.range(3, 6)), margin: 1.4 });

    // He lies up on the NORTH side of a house: whoever comes up from the south sees the house first.
    const his = houses[g.rng.int(Math.max(1, houses.length))];
    const spot = his ? standNear(g, his.x + g.rng.range(-1.5, 1.5), his.z - 3.6, 3) : null;
    if (!his || !spot) g.map.problems.push('downed-pilot: nowhere among the ruins for the pilot to hide');
    const pilot = { x: spot?.[0] ?? g.rect.x0 + 6, z: spot?.[1] ?? g.rect.z0 + 6 };
    g.map.captives.push({ id: FIRST_CAPTIVE_ID, kind: 'pilot', ...pilot });
    keepClear(g, pilot, PRIVACY);

    const rules: TimedRule[] = [];
    const kind = g.elite || g.threat >= 7 ? 'elite' : 'rifleman';
    for (let wave = 0; wave < wavesFor(g.threat); wave++) {
      searchParty(g, {
        focus: pilot, half: { x: 0, z: 0 }, standOff: 1.8, men: menPerWave(g.threat), kind, tag: tagOf(wave), laps: 2, wave,
      });
      rules.push({
        type: 'timedEvent', atSeconds: 300 + wave * WAVE_GAP, effect: 'searchPartyArrives', wakes: tagOf(wave),
        orders: 'SEARCH PARTIES ARE COMING FOR THE PILOT, WAVE AFTER WAVE: IF ONE REACHES HIM BEFORE YOU DO, HE IS LOST',
        warning: wave === 0 ? 'ENEMY SEARCH PARTY DUE IN' : 'THE NEXT SEARCH PARTY IS DUE IN',
        reason: 'THE SEARCH PARTY FOUND THE PILOT FIRST',
      });
    }
    const markerId = his?.id ?? -1;
    return {
      objective: {
        kind: 'freeAndEscort', label: 'BRING OUT THE PILOT', targetPropIds: [markerId], minFraction: 1,
        useLabel: 'GET THE PILOT ON HIS FEET', targetName: 'THE PILOT',
        rules: [...rules, RECALL],
      },
      finish(def) {
        flagTargets(def, [markerId]);
        const first = carefulSeconds(def, def.player, pilot);
        searchRules(def).forEach((rule, wave) => { rule.atSeconds = first + wave * WAVE_GAP; });
      },
    };
  },

  asserts(def, probe) {
    const { check, out } = checker('downed-pilot');
    const o = def.objective;
    const [pilot] = def.captives;
    check(o.kind === 'freeAndEscort' && o.targetName === 'THE PILOT', 'objective must be freeAndEscort, and name the pilot');
    check(def.captives.length === 1 && pilot?.kind === 'pilot', 'needs exactly one captive, the pilot');
    const ruins = def.props.filter((p) => p.kind === 'ruinHouse' && insideRect(def.goalZone, p.x, p.z));
    check(ruins.length >= 3, `the hamlet needs at least 3 ruined houses, has ${ruins.length}`);
    const marker = def.props.find((p) => p.id === o.targetPropIds[0]);
    check(marker?.kind === 'ruinHouse' && marker.target === true, 'the ruin he hides behind must be marked on the strip map');
    const rules = searchRules(def);
    check(rules.length === wavesFor(def.threat), `needs ${wavesFor(def.threat)} search waves at threat ${def.threat}`);
    if (pilot) {
      check(probe.standable({ x0: pilot.x - 0.6, z0: pilot.z - 0.6, x1: pilot.x + 0.6, z1: pilot.z + 0.6 }) >= 1, 'the pilot must be on open ground');
      check(marker !== undefined && Math.abs(marker.x - pilot.x) <= 7 && Math.abs(marker.z - pilot.z) <= 7, 'the pilot must be hidden in the ruins');
      if (marker) check(probe.breach(marker.id) === 0, 'the ruins must be reachable without crossing mines or wire');
      // ROUND8: a dog holds no post - it walks its handler's beat, as the patrols this leaves out do.
      const close = def.spawns.filter((s) => s.dormantUntil === undefined && s.patrol === undefined
        && s.kind !== 'dog'
        && (s.x - pilot.x) * (s.x - pilot.x) + (s.z - pilot.z) * (s.z - pilot.z) < EXPANSION.foundRadius * EXPANSION.foundRadius);
      check(close.length === 0, 'no sentry may be posted within finding distance of the pilot');
      check(fairTimer(def, rules[0], def.player, pilot), 'the first search party must not come before a careful man can reach him');
      rules.forEach((rule, wave) => {
        const party = wokenBy(def, rule.wakes ?? '');
        check(party.length === menPerWave(def.threat), `wave ${wave + 1} must number ${menPerWave(def.threat)}`);
        const reach = EXPANSION.foundRadius;
        const finds = (s: { patrol?: Array<[number, number]> }): boolean =>
          (s.patrol ?? []).some(([x, z]) => (x - pilot.x) * (x - pilot.x) + (z - pilot.z) * (z - pilot.z) <= reach * reach);
        check(party.some(finds), `wave ${wave + 1}'s beat must pass within ${reach} m of the hiding place`);
      });
    }
    rules.forEach((rule, wave) => {
      check(wave === 0 || rule.atSeconds > (rules[wave - 1]?.atSeconds ?? 0), 'the waves must come one after another');
      check(
        rule.orders !== undefined && rule.warning !== undefined && rule.reason !== undefined,
        'every wave must carry its orders, its countdown and its reason',
      );
    });
    return out;
  },
};
